import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { DocumentsAPI } from 'api/document/document-core';
import { ProjectId } from 'api/models';
import { NewTaskPopup } from 'components/molecules/new-task-popup';
import { useCurrentSpace } from 'components/organisms/spaces/context';
import { FetchStatus } from 'data/core';
import { appendToTasksDocMap } from 'data/documents';
import { saveTasks } from 'data/tasks/slice';
import { useMentionsData } from 'hooks/use-mentions-data';
import { useOnUserMention } from 'hooks/use-on-user-mention';
import { MentionsDataParam } from 'vendor/ckeditor/plugins/mention/constants';
import { MentionType } from 'vendor/ckeditor/plugins/mention/types';
import { updateActiveTab, useDocuments } from '../../document-store';
import { TabNames } from '../../document.types';

interface NewTaskPopupProps {
  docTitle: string;
  projectId: number;
  docId: number;
  showTaskForm: boolean;
  docRefText: string;
  onClose: () => void;
  onTaskCreated: (res) => void;
}

export const DocumentTaskPopup: FC<NewTaskPopupProps> = ({
  children,
  docTitle,
  projectId,
  docId,
  showTaskForm,
  docRefText,
  onTaskCreated,
  onClose,
}) => {
  const [state, docDispatch] = useDocuments();

  const dispatch = useDispatch();

  const { private: isPrivateSpace } = useCurrentSpace();
  const isTaskPrivate =
    isPrivateSpace || state.editor?.id === state.allEditors?.PRIVATE?.id;

  const mentionData = useMentionsData(ProjectId(projectId));
  const onUserMention = useOnUserMention(ProjectId(projectId));

  const url = window.location.href;

  /*
   We need spaceTabId information from the URl which would be of the format : http://localhost:3000/projects/2/spaces/103/tabs/3/
   Split url array : ['http:', '', 'localhost:3000', 'projects', '2', 'spaces', '103', 'tabs', '3', '']
   8th index of split url array has the spaceTabId information.
  */

  let spaceTabId;

  const splitUrl = url.split('/');

  if (
    splitUrl.length === 10 &&
    splitUrl[7] === 'tabs' &&
    typeof Number(splitUrl[8]) === 'number'
  ) {
    // eslint-disable-next-line prefer-destructuring
    spaceTabId = splitUrl[8];
  }

  const linkToDoc = `<p>From the document: <a
  href="#"
  className="rl__mention"
  data-mention=${docTitle}
  ${MentionsDataParam.Type}=${MentionType.SpaceTab}
  ${MentionsDataParam.ProjectId}=${projectId}
  ${MentionsDataParam.ObjectId}=${spaceTabId}
>${docTitle}</a></p>`;

  const onFormSubmit = (task) => {
    const descriptionWithDocRef = `
    <blockquote>
      <p>${docRefText}</p>
    </blockquote>
    <blockquote>
      ${linkToDoc}
    </blockquote>
    ${task.taskDescription}`;

    // If teams/users not present in req, api fails.
    // TODO: Once API fix this, remove the spreading.
    DocumentsAPI.createTask(
      projectId,
      docId,
      {
        ...task,
        taskDescription: descriptionWithDocRef,
        private: isTaskPrivate,
      },
      {
        docRefText: descriptionWithDocRef,
      },
    ).then((response) => {
      const taskId = `rl-task-${response?.taskId}`;
      const options = {
        taskId,
      };

      dispatch(
        appendToTasksDocMap({
          [response?.taskId]: {
            content: state.balloonToolbarDetails.type,
            isActive: true,
            parentDocId: docId,
          },
        }),
      );

      onTaskCreated(response);

      state.editor?.execute('insertTaskCommand', options);
      state.editor?.editing.view.focus();
      dispatch(
        saveTasks({ data: [response], fetchStatus: FetchStatus.Loaded }),
      );
      updateActiveTab(docDispatch, TabNames.Tasks);
    });
  };

  return showTaskForm ? (
    <NewTaskPopup
      isPrivate={isTaskPrivate}
      projectId={projectId}
      mentionData={mentionData}
      onUserMention={onUserMention}
      visible={showTaskForm}
      onSubmit={onFormSubmit}
      onClose={onClose}
    >
      {children}
    </NewTaskPopup>
  ) : null;
};
